import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SpinningWheel } from "@/components/SpinningWheel";
import { selectPrize } from "@/utils/prizeUtils";
import { useToast } from "@/hooks/use-toast";
import { initTlBox } from "@/utils/gsapAnimations_box";
import { useQueryClient } from "@tanstack/react-query";
import { gsap } from "gsap";
import { BackgroundAnimation } from "@/components/prize/BackgroundAnimation";
import { PrizeDisplay } from "@/components/prize/PrizeDisplay";
import { PrizeAnimation } from "@/components/prize/PrizeAnimation";
import { useWindowSize } from "@/hooks/useWindowSize";
import type { Prize } from "@/types";

export default function SpinToWin() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showPrizeContent, setShowPrizeContent] = useState(false);
  const [prize, setPrize] = useState<Prize | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const tipId = location.state?.tipId;
  const queryClient = useQueryClient();
  const windowSize = useWindowSize();

  const tl_lockup_Price = useRef<gsap.core.Timeline | null>(null);
  const tl_loop2 = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    if (!tipId) {
      navigate('/');
      return;
    }
  }, [tipId, navigate]);

  useEffect(() => {
    console.log("Initializing and playing the GSAP animation");
    initTlBox();


    tl_lockup_Price.current = gsap.timeline({ repeat: 0, paused: true });
    tl_loop2.current = gsap.timeline({ repeat: -1, pause: true });

    tl_lockup_Price.current
      .to("#button_spin, #wheel, .prizes", { opacity: 1, duration: 0.1 })
      .from("#Price_KV-1", { opacity: 0, scale: 0, duration: 1.5, ease: "power1.inOut" }, "+=0.0")
      .from("#Price_KV-1B", { opacity: 0, scale: 0, duration: 1.5, ease: "power1.inOut" }, "-=1.3")
      .from("#Price_KV-4", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.0")
      .from("#Price_KV-3", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.3")
      .from("#Price_KV-5, #Price_KV-2", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
      .from("#Price_KV-6", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
      .from("#Price_KV-7", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=1.4")
      .from(".wheelG", { scale: 0, duration: 2.5, ease: "elastic.out(1, 0.4)" }, "-=0.3")
      .from(".wheelG", { rotation: "360", duration: 15, ease: "power1.in" }, "-=4.2")
      .from("#btn1_G_Price", { scale: 0, duration: 1.5, ease: "elastic.out(1, 0.5)" }, "-=12.0")
      .from(".spinCopy", { y: 20, opacity: 0, duration: 2, ease: "elastic.out(1, 0.5)" }, "-=11.5")
      .eventCallback("onComplete", () => {
        tl_loop2.current?.to(".wheelG", { rotation: "-360", duration: 15, ease: "linear" }, "-=0");
      });

    const tl_prizes = gsap.timeline({ repeat: 0, paused: true });
    tl_prizes
      .from("#prize8", { transformOrigin: "70% 47%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "+=3")
      .from("#prize7", { transformOrigin: "63% 34%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize6", { transformOrigin: "50% 29%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize5", { transformOrigin: "33% 36%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize4", { transformOrigin: "31% 51%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize3", { transformOrigin: "38% 65%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize2", { transformOrigin: "52% 70%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7")
      .from("#prize1", { transformOrigin: "67% 62%", scale: 0, duration: 0.8, ease: "elastic.out(1, 0.3)" }, "-=0.7");

    const tl_lockup_Price2 = gsap.timeline({ repeat: 0, paused: true });
    tl_lockup_Price2
      .fromTo("#Price_KV", { scale: 1 }, { scale: 0.5, duration: 1.5, y:"-2vh", ease: "power1.inOut" }, "1.5");

    const tl_yoyo_Price = gsap.timeline({ repeat: -1, yoyo: true });
    tl_yoyo_Price.fromTo("#Price_KV-5_G", { scale: 1.0 }, { scale: 1.1, duration: 1.5, ease: "power1.inOut" });

    const tl_yoyo2_Price = gsap.timeline({ repeat: -1, yoyo: true });
    tl_yoyo2_Price
      .to("#Price_KV-6-7_G", { scale: 1.07, duration: 0.4, ease: "power1.inOut", delay: 10 })
      .to("#Price_KV-6-7_G", { scale: 1.0, duration: 0.3, ease: "power1.inOut" });

    tl_lockup_Price.current.play();
    tl_lockup_Price2.play();
    tl_prizes.play();

    return () => {
      // Cleanup on unmount
      tl_lockup_Price.current?.kill();
      tl_loop2.current?.kill();
      tl_prizes.kill();
      tl_lockup_Price2.kill();
      tl_yoyo_Price.kill();
      tl_yoyo2_Price.kill();
    };
  }, []);

  useEffect(() => {
    if (showPrizeContent) {
      setShowConfetti(true);
      setIsClosing(false);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showPrizeContent]);

  const handleSpinComplete = async () => {
    try {
      console.log("Spinning complete, selecting prize for tip:", tipId);

      tl_lockup_Price.current?.kill();
      tl_loop2.current?.kill();

      const wonPrize = await selectPrize(tipId);

      if (wonPrize) {
        console.log("Prize won:", wonPrize);
        setPrize(wonPrize);
        setShowPrizeContent(true);
      }
    } catch (error) {
      console.error("Error in spin completion:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong while selecting your prize. Please try again.",
      });
      navigate('/');
    }
  };

  const handlePrizeClaim = async () => {
    console.log('Handling prize claim and dialog close');
    setIsClosing(true);

    const contentOverlay = document.createElement('div');
    contentOverlay.style.position = 'fixed';
    contentOverlay.style.top = '0';
    contentOverlay.style.left = '0';
    contentOverlay.style.width = '100%';
    contentOverlay.style.height = '100%';
    contentOverlay.style.backgroundColor = 'white';
    contentOverlay.style.opacity = '0';
    contentOverlay.style.transition = 'opacity 0.3s ease';
    contentOverlay.style.zIndex = '50';
    document.body.appendChild(contentOverlay);

    requestAnimationFrame(() => {
      contentOverlay.style.opacity = '1';

      setTimeout(async () => {
        setShowPrizeContent(false);
        await queryClient.invalidateQueries({ queryKey: ['approved-tips'] });
        await queryClient.refetchQueries({ queryKey: ['approved-tips'] });

        contentOverlay.style.opacity = '0';
        setTimeout(() => {
          contentOverlay.remove();
          setIsClosing(false);
          navigate('/');
        }, 0);
      }, 0);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <BackgroundAnimation id="Price_KV" />
          <SpinningWheel onSpinComplete={handleSpinComplete} isSpinning={isSpinning} />
        </div>

        {showPrizeContent && (
          <>
            <PrizeDisplay 
              prize={prize}
              onClaim={handlePrizeClaim}
              isClosing={isClosing}
            /> 
            <PrizeAnimation 
              showConfetti={showConfetti}
              windowSize={windowSize}
            />
          </>
        )}
      </div>
    </div>
  );
}
